import { Header } from "@/components/mentor-scoring/header.tsx";
import { useUser } from "@/lib/api/user.ts";
import { requirePermission } from "@/lib/casl/route-guards";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { DataTable } from "@/components/data-table/data-table";
import { columns } from "@/components/mentor-scoring/scolumns";
import { useGetMentorshipScores } from "@/lib/api/mentor-scoring";
import { useDataTable } from "@/lib/hooks/use-data-table";
import { useRouter, useSearch } from "@tanstack/react-router";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts"
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { format } from "date-fns";

export const Route = createFileRoute("/_authenticated/mentor-scoring/$mentorId")({
  component: UserDetailsPage,
  beforeLoad: ({ context }) => {
    requirePermission(context.user, "read", "mentor-scoring");
  },
});

function UserDetailsPage() {
  const router = useRouter();
  const { mentorId } = useParams({
    strict: false,
  }) as { mentorId: string };
  const { page, per_page, search, customFilter, group, status, year, month } = useSearch({
      strict: false,
    }) as Record<string, string>;
  
    const currentPage = parseInt(page || "1", 10);
    const pageSize = parseInt(per_page || "20", 10);
  
    const { data } = useGetMentorshipScores(
      currentPage,
      pageSize,
      search,
      customFilter,
      group,
      status,
      year,
      month,
      false,
      mentorId,
    );

    const { table } = useDataTable({
      columns,
      data: data?.scores || [],
      defaultPerPage: pageSize,
      pageCount: -1,
      filterFields: [],
    });
  
    const handlePageChange = (newPageIndex: number) => {
      const newPage = newPageIndex + 1;
      router.navigate({
        to: router.state.location.pathname,
        search: (prev: any) => ({
          ...prev,
          page: newPage,
        }),
      });
    };
  
    const handlePageSizeChange = (newPageSize: number) => {
      router.navigate({
        to: router.state.location.pathname,
        search: (prev: any) => ({
          ...prev,
          page: 1,
          per_page: newPageSize,
        }),
      });
    };

  const { data: user, isPending, isError } = useUser(mentorId);

  if (isPending) {
    return (
      <div className="h-screen-nav flex h-screen w-full items-center justify-center bg-primary-100">
        <Loader2 className="h-12 w-12 animate-spin text-blue-brand" />
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className="h-screen-nav flex h-screen w-full items-center justify-center bg-primary-100">
        <div className="text-blue-brand">Some errors occurred!</div>
      </div>
    );
  }
  const nawData = table.getRowModel().rows.map(row => ({
    month: row.original.snapshot_date,
    score: row.original.final_score,
  }));

  const chartData = [...nawData]
    .sort((a, b) => a.month.localeCompare(b.month)) // String compare ISO dates
    .map(item => ({
      ...item,
      formattedMonth: format(new Date(item.month), 'MMM d') // Optional display format
  }));

  const chartConfig = {
    score: {
      label: "Score",
      color: "#2563eb",
    },
  } satisfies ChartConfig
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto space-y-6 px-4 py-6">
        <Header user={user} />
        <ChartContainer config={chartConfig} className="h-[400px] w-full">
          <LineChart
            accessibilityLayer
            data={chartData}
            margin={{
              left: 12,
              right: 12,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) =>  format(new Date(value), 'MMM d')}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => value}
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
            />
            <Line
              dataKey="score"
              type="natural"
              stroke="var(--color-score)"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ChartContainer>
        <DataTable
        table={table}
        getCanNextPage={() => {
          return !data?.isLastPage;
        }}
        getCanPreviousPage={() => {
          return currentPage > 1;
        }}
        isPending={isPending}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      >
      </DataTable>
      </div>
    </div>
  );
}
